/** Compact Three.js viewer: geometry, scalar coloring, cell-edge overlay. */

import {
  AmbientLight,
  BufferAttribute,
  BufferGeometry,
  Color,
  DirectionalLight,
  DoubleSide,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  MeshStandardMaterial,
  PerspectiveCamera,
  Scene,
  Sphere,
  Vector3,
  WebGLRenderer,
  WireframeGeometry,
} from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";

import { buildLUT, ColormapName } from "./colormap.js";
import type { Geometry, ScalarArray } from "./api.js";

/** Map per-vertex scalar *values* into a flat RGB Float32Array via a LUT.
 *
 * Pure + side-effect-free so it can be unit-tested without a GPU context.
 */
export function computeVertexColors(
  values: Float32Array,
  range: [number, number],
  lut: Float32Array,
): Float32Array {
  const [lo, hi] = range;
  const span = hi - lo || 1;
  const colors = new Float32Array(values.length * 3);
  for (let i = 0; i < values.length; i++) {
    let t = (values[i] - lo) / span;
    t = t < 0 ? 0 : t > 1 ? 1 : t;
    const idx = Math.round(t * 255) * 3;
    colors[i * 3] = lut[idx];
    colors[i * 3 + 1] = lut[idx + 1];
    colors[i * 3 + 2] = lut[idx + 2];
  }
  return colors;
}

export class Viewer {
  readonly scene = new Scene();
  private camera: PerspectiveCamera;
  private renderer: WebGLRenderer;
  private controls: OrbitControls;
  private mesh: Mesh | null = null;
  private geometry: BufferGeometry | null = null;
  private material: MeshStandardMaterial;
  private edges: LineSegments | null = null;
  private showEdges = false;

  constructor(private canvas: HTMLCanvasElement) {
    this.scene.background = new Color(0x1a1a1f);
    this.camera = new PerspectiveCamera(45, 1, 0.001, 1e6);
    this.camera.position.set(2, 2, 2);
    this.renderer = new WebGLRenderer({ canvas, antialias: true, preserveDrawingBuffer: true });
    this.controls = new OrbitControls(this.camera, canvas);
    this.controls.enableDamping = true;

    this.scene.add(new AmbientLight(0xffffff, 0.6));
    const key = new DirectionalLight(0xffffff, 0.8);
    key.position.set(1, 1, 1);
    this.scene.add(key);
    const fill = new DirectionalLight(0xffffff, 0.3);
    fill.position.set(-1, -0.5, -1);
    this.scene.add(fill);

    this.material = new MeshStandardMaterial({
      color: 0xbfc3c9,
      metalness: 0.1,
      roughness: 0.75,
      side: DoubleSide,
      flatShading: false,
      vertexColors: false,
    });

    this.resize();
    window.addEventListener("resize", () => this.resize());
    this.animate();
  }

  private animate = (): void => {
    requestAnimationFrame(this.animate);
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  };

  resize(): void {
    const w = this.canvas.clientWidth || 1;
    const h = this.canvas.clientHeight || 1;
    this.renderer.setPixelRatio(window.devicePixelRatio || 1);
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  /** Replace the rendered geometry from parsed buffers. */
  setGeometry(geom: Geometry): void {
    this.disposeMesh();
    const g = new BufferGeometry();
    g.setAttribute("position", new BufferAttribute(geom.vertices, 3));
    g.setIndex(new BufferAttribute(geom.faces, 1));
    if (geom.normals) g.setAttribute("normal", new BufferAttribute(geom.normals, 3));
    else g.computeVertexNormals();
    this.geometry = g;
    this.material.vertexColors = false;
    this.mesh = new Mesh(g, this.material);
    this.scene.add(this.mesh);
    this.rebuildEdges();
    this.fit();
  }

  /** Color by a per-point scalar array (or clear with `null`). */
  setColorBy(scalar: ScalarArray | null, colormap: ColormapName, range?: [number, number]): void {
    if (!this.geometry) return;
    if (scalar === null || scalar.association !== "points") {
      this.geometry.deleteAttribute("color");
      this.material.vertexColors = false;
      this.material.color = new Color(0xbfc3c9);
      this.material.needsUpdate = true;
      return;
    }
    const rng = range ?? scalar.range;
    const colors = computeVertexColors(scalar.values, rng, buildLUT(colormap));
    this.geometry.setAttribute("color", new BufferAttribute(colors, 3));
    this.material.vertexColors = true;
    this.material.color = new Color(0xffffff);
    this.material.needsUpdate = true;
  }

  /** Toggle the cell-edge (wireframe) overlay. */
  setShowEdges(enabled: boolean): void {
    this.showEdges = enabled;
    this.rebuildEdges();
  }

  getShowEdges(): boolean {
    return this.showEdges;
  }

  private rebuildEdges(): void {
    if (this.edges) {
      this.scene.remove(this.edges);
      this.edges.geometry.dispose();
      (this.edges.material as LineBasicMaterial).dispose();
      this.edges = null;
    }
    if (!this.showEdges || !this.geometry) return;
    const mat = new LineBasicMaterial({ color: 0x11151a, transparent: true, opacity: 0.55 });
    this.edges = new LineSegments(new WireframeGeometry(this.geometry), mat);
    this.scene.add(this.edges);
  }

  /** Frame the current mesh in the camera. */
  fit(): void {
    if (!this.geometry) return;
    this.geometry.computeBoundingSphere();
    const s: Sphere | null = this.geometry.boundingSphere;
    if (!s) return;
    const r = s.radius || 1;
    const dir = new Vector3(1, 0.7, 1).normalize();
    this.camera.position.copy(s.center.clone().add(dir.multiplyScalar(r * 3)));
    this.controls.target.copy(s.center);
    this.camera.near = r / 100;
    this.camera.far = r * 100;
    this.camera.updateProjectionMatrix();
    this.controls.update();
  }

  screenshot(): string {
    this.renderer.render(this.scene, this.camera);
    return this.renderer.domElement.toDataURL("image/png");
  }

  private disposeMesh(): void {
    if (this.edges) {
      this.scene.remove(this.edges);
      this.edges.geometry.dispose();
      (this.edges.material as LineBasicMaterial).dispose();
      this.edges = null;
    }
    if (this.mesh) {
      this.scene.remove(this.mesh);
      this.mesh = null;
    }
    if (this.geometry) {
      this.geometry.dispose();
      this.geometry = null;
    }
  }
}
